import React, { useState } from 'react';
import { getAccentColor } from '../lib/jlfcTheme';
import {
  AppShell,
  Group,
  Text,
  Button,
  ScrollArea,
  Badge,
  Box,
  Select,
  useMantineTheme,
  Anchor,
} from '@mantine/core';
import {
  IconLogout,
  IconMenu2,
  IconMenuDeep,
  IconClipboardCheck,
  IconKey,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import DynamicMenu from '../components/DynamicMenu';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ReleaseNotesModal from '../components/ReleaseNotesModal';
import AppMain from './AppMain';
import { useActiveExploitant } from '../context/ActiveExploitantContext';
import { APP_VERSION, MANTINE_VERSION } from '../lib/appVersion';

export default function AppLayout({ user, onLogout, onUserUpdate }) {
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();
  const palette = theme.colors[theme.primaryColor] || theme.colors.teal;
  const { isRoot, exploitants, loading: loadingExploitants, activeId, selectExploitant, activeExploitant } =
    useActiveExploitant();

  const exploitantOptions = exploitants.map((e) => ({
    value: String(e.id),
    label: `${e.code} — ${e.raison_sociale}`,
  }));

  const displayName =
    user.prenom && user.nom ? `${user.prenom} ${user.nom}` : user.login;

  const tenantLabel = user.exploitant?.raison_sociale || activeExploitant?.raison_sociale;

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: menuCollapsed ? 60 : 250,
        breakpoint: 'sm',
      }}
      footer={{ height: 50 }}
      padding="md"
      styles={{
        root: { height: '100dvh', overflow: 'hidden' },
        main: {
          // Mantine fixe min-height: 100dvh sur main → contenu coupé sans scroll
          minHeight: 0,
          height: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        navbar: { transition: 'width 0.3s ease' },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setMenuCollapsed((c) => !c)}
              title={menuCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
            >
              {menuCollapsed ? <IconMenuDeep size={18} /> : <IconMenu2 size={18} />}
            </Button>
            <Group gap="xs" wrap="nowrap">
              <IconClipboardCheck size={22} color={palette[6]} stroke={1.75} />
              <Text fw={700} size="sm" visibleFrom="xs">
                JFL Contrôles
              </Text>
            </Group>
          </Group>

          <Group gap="sm" wrap="nowrap">
            {isRoot ? (
              <Select
                placeholder="Choisir une exploitation…"
                data={exploitantOptions}
                value={activeId}
                onChange={(value) => selectExploitant(value)}
                searchable
                clearable
                nothingFoundMessage="Aucune exploitation"
                disabled={loadingExploitants}
                w={280}
                size="sm"
                comboboxProps={{ withinPortal: true }}
                aria-label="Exploitation active"
              />
            ) : null}
            {tenantLabel ? (
              <Badge variant="light" color={getAccentColor()} visibleFrom="md">
                {tenantLabel}
              </Badge>
            ) : null}
            {isRoot && !activeId ? (
              <Badge variant="light" color="orange" visibleFrom="sm">
                Aucune exploitation sélectionnée
              </Badge>
            ) : null}
            <Text size="sm" c="dimmed" visibleFrom="sm" style={{ cursor: 'pointer' }} onClick={() => navigate('/profil')}>
              {displayName}
            </Text>
            <Button
              variant="light"
              size="sm"
              leftSection={<IconKey size={16} />}
              onClick={() => setPasswordModalOpen(true)}
              title="Changer mon mot de passe"
            >
              <Box component="span" visibleFrom="sm">
                Mot de passe
              </Box>
            </Button>
            <Button
              variant="light"
              color="gray"
              size="sm"
              leftSection={<IconLogout size={16} />}
              onClick={onLogout}
            >
              <Box component="span" visibleFrom="sm">
                Déconnexion
              </Box>
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p={menuCollapsed ? 'xs' : 'md'}>
        {!menuCollapsed && (
          <ScrollArea flex={1} type="auto" offsetScrollbars>
            <DynamicMenu
              onSelectModule={(path) => navigate(path)}
              currentPath={location.pathname}
              openMenus={openMenus}
              setOpenMenus={setOpenMenus}
              openSubMenus={openSubMenus}
              setOpenSubMenus={setOpenSubMenus}
            />
          </ScrollArea>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <Box
          style={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <AppMain
            key={isRoot ? activeId || 'root-global' : 'tenant'}
            user={user}
            onUserUpdate={onUserUpdate}
          />
        </Box>
      </AppShell.Main>

      <AppShell.Footer>
        <Group h="100%" px="md" justify="center">
          <Text size="xs" c="dimmed">
            © {new Date().getFullYear()} JFL Contrôles — KODALA.dev ·{' '}
            <Anchor component="button" size="xs" c={getAccentColor()} onClick={() => setReleaseNotesOpen(true)}>
              v{APP_VERSION} — Release notes
            </Anchor>{' '}
            · React &
            Mantine {MANTINE_VERSION}
          </Text>
        </Group>
      </AppShell.Footer>

      <ChangePasswordModal
        opened={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onChanged={onLogout}
      />
      <ReleaseNotesModal
        opened={releaseNotesOpen}
        onClose={() => setReleaseNotesOpen(false)}
        appVersion={APP_VERSION}
      />
    </AppShell>
  );
}
